"use client";

import type React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Search,
  ChevronDown,
  ChevronUp,
  X,
} from "lucide-react";

// Import product data
import productsData from "../data/products.json";
import { PRODUCT_CATEGORY_STRUCTURE } from "../data/ProductCategories";

interface Product {
  id: string;
  name: string;
  category: string;
  subcategory?: string;
  productType?: string;
  secondaryCategories?: ProductCategoryPlacement[];
  description_extended?: string;
  tags?: string[];
  image: string;
  images?: string[];
  description: string;
  datasheet?: string;
  safetySheet?: string;
  sourceUrl?: string;
  active?: boolean;
}

interface ProductCategoryPlacement {
  category: string;
  subcategory?: string;
  productType?: string;
}

interface FilterState {
  categories: string[];
  subcategories: string[];
  productTypes: string[];
  tags: string[];
}

const products = (productsData as Product[]).filter((product) => product.active !== false);

const normalize = (value: unknown) => String(value ?? "").toLowerCase();
const normalizeTag = (value: unknown) => normalize(value).replace(/\s+/g, " ").trim();
const normalizeSearchText = (value: unknown) =>
  normalize(value)
    .replace(/[–—−]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
const getCurrentFilterValue = (values: string[]) => {
  const cleanedValues = values.map(normalizeTag).filter(Boolean);
  return cleanedValues.length > 0 ? [cleanedValues[cleanedValues.length - 1]] : [];
};
const sanitizeFilters = (value: FilterState): FilterState => ({
  categories: getCurrentFilterValue(value.categories),
  subcategories: getCurrentFilterValue(value.subcategories),
  productTypes: getCurrentFilterValue(value.productTypes),
  tags: getCurrentFilterValue(value.tags),
});
const getFiltersFromSearchParams = (params: URLSearchParams): FilterState => ({
  categories: params.get("category") ? [normalizeTag(params.get("category"))] : [],
  subcategories: params.get("subcategory") ? [normalizeTag(params.get("subcategory"))] : [],
  productTypes: params.get("type") ? [normalizeTag(params.get("type"))] : [],
  tags: params.get("tag") ? [normalizeTag(params.get("tag"))] : [],
});
const getSortFromSearchParams = (params: URLSearchParams) =>
  params.get("sort") === "relevance" || params.get("sort") === "newest"
    ? params.get("sort") as "relevance" | "newest"
    : "type";
const isDownloadableDocument = (href?: string) =>
  Boolean(href && (href.startsWith("http") || href.startsWith("/pdf/")));
const hasLocalPds = (product: Product) =>
  Boolean(product.datasheet && product.datasheet.startsWith("/pdf/pds/"));
const normalizeGrade = (value: unknown) =>
  normalize(value).replace(/[\s-]+/g, "").trim();
const viscosityPatterns = [
  /\b\d{1,2}\s*w\s*-?\s*\d{0,2}\b/gi,
  /\bsae\s*\d{2}\b/gi,
  /\biso(?:\s*vg|\s*grade)?\s*\d{2,3}\b/gi,
];
const isViscosityQuery = (value: string) =>
  viscosityPatterns.some((pattern) => {
    pattern.lastIndex = 0;
    return pattern.test(value);
  });

const importantTagTerms = [
  "engine oil",
  "diesel",
  "gasoline",
  "petrol",
  "motorcycle",
  "scooter",
  "transmission fluid",
  "automatic transmission",
  "manual transmission",
  "gear oil",
  "automotive gear oil",
  "brake fluid",
  "diesel exhaust fluid",
  "def",
  "hydraulic",
  "hydraulic oil",
  "industrial gear oil",
  "marine",
  "system oil",
  "cylinder oil",
  "trunk piston",
  "marine diesel",
  "marine system oil",
  "marine cylinder oil",
  "marine trunk piston engine oil",
  "two-stroke",
  "two stroke",
  "four-stroke",
  "four stroke",
  "medium speed",
  "medium-speed",
  "high speed",
  "high-speed",
  "lng",
  "dual fuel",
  "low sulfur",
  "high sulfur",
  "seca",
  "electronic injection",
  "man",
  "long stroke",
  "grease",
  "multipurpose grease",
  "automotive grease",
  "bearing grease",
  "synthetic grease",
  "lithium grease",
  "lithium",
  "lithium complex",
  "calcium",
  "polyurea",
  "mos2",
  "moly",
  "organic thickener",
  "turbine oil",
  "compressor",
  "compressor oil",
  "heat transfer oil",
  "metalworking",
  "cutting oil",
  "cutting fluid",
  "quenching oil",
  "rust preventive",
  "rust resistance",
  "wire rope",
  "slideway oil",
  "rock drill",
  "bearing oil",
  "fire-resistant",
  "synthetic",
  "pao",
  "ep",
  "extreme pressure",
  "anti-wear",
  "oxidation",
  "oxidation resistance",
  "corrosion",
  "demulsibility",
  "antifoaming",
  "air release",
  "water resistance",
  "shock loading",
  "high temperature",
  "low temperature",
  "adhesion",
  "sealing",
  "pumpability",
  "anti-seize",
  "food grade",
  "food machine",
  "chassis",
  "mining",
  "open gear",
  "roller bearing",
  "aviation",
  "cable protection",
  "steel industry",
  "ammonia",
  "refrigeration",
  "wind turbine",
  "railroad",
  "tractor",
  "detergent",
  "final drive",
  "axle oil",
  "limited slip",
  "passenger car",
  "commercial vehicle",
  "construction machinery",
  "industrial machinery",
];

const ProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get("q") || "";
  const sortOption = getSortFromSearchParams(searchParams);
  
  // Screen size state
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(window.innerWidth < 768);
  
  // Pagination state
  const [itemsPerPage, setItemsPerPage] = useState<number>(isSmallScreen ? 10 : 25);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Expanded sections state
  const [expandedSections, setExpandedSections] = useState({
    category: false,
    subcategory: false,
    productType: false,
    tag: false,
  });

  const getProductType = (product: Product) => product.productType || product.subcategory || "";
  const getPlacementType = (placement: ProductCategoryPlacement) =>
    placement.productType || placement.subcategory || "";
  const getProductCategoryPlacements = (product: Product): ProductCategoryPlacement[] => [
    {
      category: product.category,
      subcategory: product.subcategory,
      productType: product.productType || product.subcategory || "",
    },
    ...(product.secondaryCategories || []),
  ];
  const getOrderIndex = (value: string | undefined, orderedValues: string[]) => {
    const index = orderedValues.findIndex((orderedValue) => normalizeTag(orderedValue) === normalizeTag(value));
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };
  const getSortPlacement = (product: Product) =>
    getProductCategoryPlacements(product).find((placement) =>
      matchesFilterValues(currentFilters.categories, placement.category) &&
      matchesFilterValues(currentFilters.subcategories, placement.subcategory) &&
      matchesFilterValues(currentFilters.productTypes, getPlacementType(placement))
    ) || getProductCategoryPlacements(product)[0];
  const compareByType = (a: Product, b: Product) => {
    const aPlacement = getSortPlacement(a);
    const bPlacement = getSortPlacement(b);
    const aSubcategories = Object.keys(PRODUCT_CATEGORY_STRUCTURE[aPlacement.category]?.subcategories || {});
    const bSubcategories = Object.keys(PRODUCT_CATEGORY_STRUCTURE[bPlacement.category]?.subcategories || {});
    const aTypes = aPlacement.subcategory
      ? PRODUCT_CATEGORY_STRUCTURE[aPlacement.category]?.subcategories[aPlacement.subcategory] || []
      : [];
    const bTypes = bPlacement.subcategory
      ? PRODUCT_CATEGORY_STRUCTURE[bPlacement.category]?.subcategories[bPlacement.subcategory] || []
      : [];

    return (
      getOrderIndex(aPlacement.category, Object.keys(PRODUCT_CATEGORY_STRUCTURE)) -
      getOrderIndex(bPlacement.category, Object.keys(PRODUCT_CATEGORY_STRUCTURE)) ||
      getOrderIndex(aPlacement.subcategory, aSubcategories) -
      getOrderIndex(bPlacement.subcategory, bSubcategories) ||
      getOrderIndex(getPlacementType(aPlacement), aTypes) -
      getOrderIndex(getPlacementType(bPlacement), bTypes) ||
      a.name.localeCompare(b.name, undefined, { numeric: true })
    );
  };
  const matchesFilterValues = (selected: string[], value: string | undefined) =>
    selected.length === 0 || selected.includes(normalize(value));
  const matchesTaxonomyFilters = (product: Product, filters: FilterState) =>
    getProductCategoryPlacements(product).some((placement) =>
      matchesFilterValues(filters.categories, placement.category) &&
      matchesFilterValues(filters.subcategories, placement.subcategory) &&
      matchesFilterValues(filters.productTypes, getPlacementType(placement))
    );
  const addTag = (tagMap: Map<string, string>, tag: unknown) => {
    const cleaned = String(tag ?? "").replace(/\s+/g, " ").trim();
    const normalized = normalizeTag(cleaned);

    if (!cleaned || !isUsefulTag(cleaned)) {
      return;
    }

    if (!tagMap.has(normalized)) {
      tagMap.set(normalized, cleaned);
    }
  };
  const isUsefulTag = (tag: string) => {
    const normalized = normalizeTag(tag);
    const hasGradeOrStandard =
      /\b\d+w-\d+\b/i.test(tag) ||
      /\bsae\s*\d+\b/i.test(tag) ||
      /\bdot\s*\d(?:\.\d)?\b/i.test(tag) ||
      /\bgl-\d\b/i.test(tag) ||
      /\bgf-\d[a-z]?\b/i.test(tag) ||
      /\biso\s*(?:grade\s*)?\d+(?:\s*to\s*\d+)?\b/i.test(tag) ||
      /\b\d+℃/i.test(tag) ||
      /\b(?:7008|7019-1|7413|7417b|gfr-154|grk-a|blc-a|blc-d|hp-r|hpxt|jz ep a|mcl-o)\b/i.test(tag) ||
      /\b(?:3005|5025|5040|5070s|5080s|50100|3012|3030|4012|4030|4040|340|380|580)\b/i.test(tag) ||
      /\b(?:c2\/sp|c3\/sp|c5\/sp|a3\/b4\/sp|sp\/gf-6a|sn\/gf-5|ck-4|ci-4|cf-4|cf-2)\b/i.test(tag) ||
      /\b(?:jaso\s*)?(?:ma2|mb)\b/i.test(tag) ||
      /\b(?:sp|sn|sl)\b/i.test(tag);
    const isKnownTerm = importantTagTerms.includes(normalized);

    return hasGradeOrStandard || isKnownTerm;
  };
  const extractPatternTags = (text: string) => {
    const collectMatches = (pattern: RegExp) =>
      Array.from(text.matchAll(pattern), (match) => match[0]);
    const saeSeries = Array.from(
      text.matchAll(/\bSAE\s*((?:\d+\s*\/\s*)+\d+)\b/gi),
      (match) => match[1]
        .split("/")
        .map((grade) => `SAE ${grade.trim()}`)
    ).flat();
    const isoSeries = Array.from(
      text.matchAll(/\bISO\s+viscosity\s+grades?\s+([\d,\sand]+)\b/gi),
      (match) => Array.from(match[1].matchAll(/\d+/g), (grade) => `ISO ${grade[0]}`)
    ).flat();

    return [
      ...collectMatches(/\b\d+W-\d+\b/gi),
      ...collectMatches(/\bSAE\s*\d+\b/gi),
      ...saeSeries,
      ...collectMatches(/\bDOT\s*\d(?:\.\d)?\b/gi),
      ...collectMatches(/\bGL-\d\b/gi),
      ...collectMatches(/\bGF-\dA?\b/gi),
      ...collectMatches(/\bISO\s+Grade\s+\d+\s+to\s+\d+\b/gi),
      ...collectMatches(/\bISO(?:\s+viscosity)?\s+(?:grades?\s+)?(?:Grade\s+)?\d+\b/gi),
      ...isoSeries,
      ...collectMatches(/\b\d+℃/g),
      ...collectMatches(/\b(?:7008|7019-1|7413|7417B|GFR-154|GRK-A|BLC-A|BLC-D|HP-R|HPXT|JZ EP A|MCL-O)\b/gi),
      ...collectMatches(/\b(?:3005|5025|5040|5070S|5080S|50100|3012|3030|4012|4030|4040|340|380|580)\b/gi),
      ...collectMatches(/\b(?:C2\/SP|C3\/SP|C5\/SP|A3\/B4\/SP|SP\/GF-6A|SN\/GF-5|CK-4|CI-4|CF-4|CF-2)\b/gi),
      ...collectMatches(/\bJASO\s*(?:MA2|MB)\b/gi),
      ...collectMatches(/\b(?:SP|SN|SL|MA2|MB)\b/g),
    ].map((match) => match.replace(/\s+/g, " ").trim());
  };
  const getProductFilterTags = (product: Product) => {
    const tagMap = new Map<string, string>();
    const combinedText = [
      product.name,
      product.category,
      product.subcategory,
      product.productType,
      ...(product.tags || []),
    ]
      .filter(Boolean)
      .join(" ");

    (product.tags || []).forEach((tag) => addTag(tagMap, tag));
    [product.subcategory, product.productType].forEach((tag) => addTag(tagMap, tag));
    extractPatternTags(combinedText).forEach((tag) => addTag(tagMap, tag));
    importantTagTerms.forEach((term) => {
      if (combinedText.toLowerCase().includes(term)) {
        const displayTerm = ["def", "pao", "ep", "lng", "seca", "man"].includes(term)
          ? term.toUpperCase()
          : term === "mos2"
            ? "MoS2"
            : term;
        addTag(tagMap, displayTerm);
      }
    });

    return Array.from(tagMap.values());
  };
  const getProductFilterTagKeys = (product: Product) =>
    getProductFilterTags(product).map(normalizeTag);
  const extractViscosityGrades = (product: Product) => {
    const gradeText = [
      product.name,
      product.productType,
      product.subcategory,
      product.description,
      product.description_extended,
      ...(product.tags || []),
    ].join(" ");
    const grades = new Set<string>();

    for (const pattern of viscosityPatterns) {
      pattern.lastIndex = 0;
      const matches = gradeText.match(pattern) || [];
      for (const match of matches) {
        const normalizedGrade = normalizeGrade(match);
        if (normalizedGrade) {
          grades.add(normalizedGrade);
        }
      }
    }

    return Array.from(grades);
  };
  const calculateViscosityScore = (product: Product, searchValue: string) => {
    const normalizedQuery = normalizeGrade(searchValue);

    if (!normalizedQuery || !isViscosityQuery(searchValue)) {
      return 0;
    }

    return Math.max(
      0,
      ...extractViscosityGrades(product).map((grade) => {
        if (grade === normalizedQuery) {
          return 900;
        }

        if (grade.startsWith(normalizedQuery)) {
          return 650;
        }

        if (grade.includes(normalizedQuery)) {
          return 100;
        }

        return 0;
      })
    );
  };
  const getSearchTerms = (searchValue: string) => {
    const fullQuery = normalizeSearchText(searchValue);
    const tokens = fullQuery
      .split(/\s+/)
      .map(normalizeSearchText)
      .filter((token) => token && (token.length > 1 || /\d/.test(token)));

    return {
      fullQuery,
      tokens: tokens.filter((token) => token !== fullQuery),
    };
  };
  const scoreFieldMatch = (
    value: unknown,
    term: string,
    scores: { exact?: number; startsWith?: number; contains?: number }
  ) => {
    const normalizedValue = normalizeSearchText(value);

    if (!normalizedValue || !term) {
      return 0;
    }

    if (normalizedValue === term) {
      return scores.exact || scores.startsWith || scores.contains || 0;
    }

    if (normalizedValue.startsWith(term)) {
      return scores.startsWith || scores.contains || 0;
    }

    if (normalizedValue.includes(term)) {
      return scores.contains || 0;
    }

    return 0;
  };
  const calculateRelevanceScore = (product: Product, searchValue: string) => {
    const { fullQuery, tokens } = getSearchTerms(searchValue);

    if (!fullQuery) {
      return 0;
    }

    const tagValues = getProductFilterTags(product);
    const viscosityQuery = isViscosityQuery(fullQuery);
    const viscosityScore = calculateViscosityScore(product, fullQuery);
    let score = 0;

    score += viscosityScore;

    if (viscosityQuery) {
      score += scoreFieldMatch(product.name, fullQuery, {
        exact: 1000,
        startsWith: 700,
        contains: viscosityScore >= 650 ? 120 : 0,
      });
      score += Math.max(
        0,
        ...tagValues.map((tag) =>
          scoreFieldMatch(tag, fullQuery, {
            exact: 450,
            contains: viscosityScore >= 650 ? 100 : 0,
          })
        )
      );
    } else {
      score += scoreFieldMatch(product.name, fullQuery, {
        exact: 1000,
        startsWith: 700,
        contains: 500,
      });
      score += Math.max(
        0,
        ...tagValues.map((tag) =>
          scoreFieldMatch(tag, fullQuery, {
            exact: 450,
            contains: 250,
          })
        )
      );
    }
    score += scoreFieldMatch(product.productType, fullQuery, {
      exact: 350,
      contains: 275,
    });
    score += scoreFieldMatch(product.subcategory, fullQuery, {
      exact: 300,
      contains: 225,
    });
    score += scoreFieldMatch(product.description, fullQuery, {
      contains: 100,
    });
    score += scoreFieldMatch(product.description_extended, fullQuery, {
      contains: 100,
    });
    score += scoreFieldMatch(product.category, fullQuery, {
      contains: 75,
    });

    for (const token of tokens) {
      const viscosityToken = isViscosityQuery(token);
      const tokenViscosityScore = calculateViscosityScore(product, token);

      score += Math.round(tokenViscosityScore * 0.6);

      score += scoreFieldMatch(product.name, token, {
        exact: 120,
        startsWith: 90,
        contains: viscosityToken && tokenViscosityScore < 650 ? 0 : 70,
      });
      score += Math.max(
        0,
        ...tagValues.map((tag) =>
          scoreFieldMatch(tag, token, {
            exact: 100,
            contains: viscosityToken && tokenViscosityScore < 650 ? 0 : 60,
          })
        )
      );
      score += scoreFieldMatch(product.productType, token, {
        exact: 80,
        contains: 55,
      });
      score += scoreFieldMatch(product.subcategory, token, {
        exact: 70,
        contains: 50,
      });
      score += scoreFieldMatch(product.description, token, {
        contains: 15,
      });
      score += scoreFieldMatch(product.description_extended, token, {
        contains: 15,
      });
      score += scoreFieldMatch(product.category, token, {
        contains: 10,
      });
    }

    return score;
  };
  const currentFilters = sanitizeFilters(getFiltersFromSearchParams(searchParams));
  const currentTempFilters = currentFilters;
  const selectedCategoryKey = currentTempFilters.categories.join("|");
  const selectedSubcategoryKey = currentTempFilters.subcategories.join("|");
  const selectedProductTypeKey = currentTempFilters.productTypes.join("|");

  // Calculate unique filter values
  const allCategories = useMemo(
    () => Array.from(new Set(products.map((product) => product.category))),
    []
  );
  const allSubcategories = useMemo(
    () => {
      const selectedCategories = selectedCategoryKey ? selectedCategoryKey.split("|") : [];

      return Array.from(
        new Set(
          products
            .flatMap((product) =>
              getProductCategoryPlacements(product)
                .filter((placement) => matchesFilterValues(selectedCategories, placement.category))
                .map((placement) => placement.subcategory)
            )
            .filter(Boolean) as string[]
        )
      );
    },
    [selectedCategoryKey]
  );
  const allProductTypes = useMemo(
    () => {
      const selectedCategories = selectedCategoryKey ? selectedCategoryKey.split("|") : [];
      const selectedSubcategories = selectedSubcategoryKey ? selectedSubcategoryKey.split("|") : [];

      return Array.from(
        new Set(
          products
            .flatMap((product) =>
              getProductCategoryPlacements(product)
                .filter((placement) =>
                  matchesFilterValues(selectedCategories, placement.category) &&
                  matchesFilterValues(selectedSubcategories, placement.subcategory)
                )
                .map((placement) => getPlacementType(placement))
            )
            .filter(Boolean)
        )
      );
    },
    [selectedCategoryKey, selectedSubcategoryKey]
  );
  const tagOptions = useMemo(
    () => {
      const selectedCategories = selectedCategoryKey ? selectedCategoryKey.split("|") : [];
      const selectedSubcategories = selectedSubcategoryKey ? selectedSubcategoryKey.split("|") : [];
      const selectedProductTypes = selectedProductTypeKey ? selectedProductTypeKey.split("|") : [];
      const tagMap = new Map<string, { label: string; count: number }>();

      products
        .filter((product) => matchesTaxonomyFilters(product, {
          categories: selectedCategories,
          subcategories: selectedSubcategories,
          productTypes: selectedProductTypes,
          tags: [],
        }))
        .forEach((product) => {
          getProductFilterTags(product).forEach((tag) => {
            const normalized = normalizeTag(tag);

            if (!normalized) {
              return;
            }

            const existing = tagMap.get(normalized);
            if (existing) {
              existing.count += 1;
              return;
            }

            tagMap.set(normalized, {
              label: tag,
              count: 1,
            });
          });
        });

      return Array.from(tagMap.values())
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))
        .slice(0, 140);
    },
    // Product tag helpers are static for this catalog; selected keys control recalculation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedCategoryKey,
      selectedSubcategoryKey,
      selectedProductTypeKey,
    ]
  );
  const allTags = tagOptions.map((tag) => tag.label);
  const tagCountMap = new Map(tagOptions.map((tag) => [normalizeTag(tag.label), tag.count]));
  const getDisplayValue = (value: string, options: string[]) =>
    options.find((option) => normalizeTag(option) === value) || value;
  const activeFilters = Array.from(
    new Map(
      [
        ...currentFilters.categories.map((value) => ({
          type: "category",
          value,
          label: getDisplayValue(value, allCategories),
        })),
        ...currentFilters.subcategories.map((value) => ({
          type: "subcategory",
          value,
          label: getDisplayValue(value, allSubcategories),
        })),
        ...currentFilters.productTypes.map((value) => ({
          type: "productType",
          value,
          label: getDisplayValue(value, allProductTypes),
        })),
        ...currentFilters.tags.map((value) => ({
          type: "tag",
          value,
          label: getDisplayValue(value, allTags),
        })),
        ...(searchTerm.trim()
          ? [{ type: "search", value: searchTerm.trim(), label: `Search: ${searchTerm.trim()}` }]
          : []),
      ].map((filter) => [`${filter.type}:${normalizeTag(filter.value)}`, filter])
    ).values()
  );
  const updateProductSearchParams = useCallback((
    updates: Partial<Record<"q" | "category" | "subcategory" | "type" | "tag" | "sort", string | null>>
  ) => {
    const nextParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (!value || (key === "sort" && value === "type")) {
        nextParams.delete(key);
        return;
      }

      nextParams.set(key, value);
    });

    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Check for product navigation values from shared category menus.
  useEffect(() => {
    // Check for localStorage values
    const storedCategory = window.localStorage.getItem("productCategory");
    if (storedCategory) {
      updateProductSearchParams({
        category: storedCategory,
        subcategory: null,
        type: null,
        tag: null,
      });
      window.localStorage.removeItem("productCategory");
    }

    // Handle subcategory if present
    const storedSubcategory = window.localStorage.getItem("productSubcategory");
    if (storedSubcategory) {
      updateProductSearchParams({
        subcategory: storedSubcategory,
        type: null,
        tag: null,
      });
      window.localStorage.removeItem("productSubcategory");
    }

    // Handle specific product if present
    const storedProduct = window.localStorage.getItem("productProduct");
    if (storedProduct) {
      const storedProductValue = storedProduct.toLowerCase();
      const matchesProductType = products.some(
        (product) => normalize(getProductType(product)) === storedProductValue
      );
      const matchesTag = products.some((product) =>
        (product.tags || []).some((tag) => normalize(tag) === storedProductValue)
      );

      if (matchesProductType) {
        updateProductSearchParams({
          type: storedProduct,
          tag: null,
        });
      } else if (matchesTag) {
        updateProductSearchParams({
          tag: storedProduct,
        });
      } else {
        updateProductSearchParams({
          q: storedProduct,
        });
      }
      window.localStorage.removeItem("productProduct");
    }

    const storedIndustry = window.localStorage.getItem("industry");
    if (storedIndustry) {
      // Handle industry filter if needed
      window.localStorage.removeItem("industry");
    }
  }, [updateProductSearchParams]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const smallScreen = window.innerWidth < 768;
      setIsSmallScreen(smallScreen);
      
      // Only update itemsPerPage if user hasn't manually changed it
      if (itemsPerPage === (isSmallScreen ? 10 : 25)) {
        setItemsPerPage(smallScreen ? 10 : 25);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isSmallScreen, itemsPerPage]);

  // Clear all filters
  const clearAllFilters = () => {
    const nextParams = new URLSearchParams(searchParams);
    ["q", "category", "subcategory", "type", "tag", "sort"].forEach((key) => {
      nextParams.delete(key);
    });
    setSearchParams(nextParams, { replace: true });
  };

  // Toggle filter section expansion
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Handle category filter changes
  const toggleCategoryFilter = (category: string) => {
    const normalizedCategory = normalizeTag(category);
    const categoryIsSelected = currentFilters.categories.includes(normalizedCategory);

    updateProductSearchParams({
      category: categoryIsSelected ? null : getDisplayValue(normalizedCategory, allCategories),
      subcategory: null,
      type: null,
      tag: null,
    });
  };

  // Handle subcategory filter changes
  const toggleSubcategoryFilter = (subcategory: string) => {
    const normalizedSubcategory = normalizeTag(subcategory);
    const subcategoryIsSelected = currentFilters.subcategories.includes(normalizedSubcategory);

    updateProductSearchParams({
      subcategory: subcategoryIsSelected ? null : getDisplayValue(normalizedSubcategory, allSubcategories),
      type: null,
      tag: null,
    });
  };

  // Handle product type filter changes
  const toggleProductTypeFilter = (type: string) => {
    const normalizedType = normalizeTag(type);
    const typeIsSelected = currentFilters.productTypes.includes(normalizedType);

    updateProductSearchParams({
      type: typeIsSelected ? null : getDisplayValue(normalizedType, allProductTypes),
      tag: null,
    });
  };

  const toggleTagFilter = (tag: string) => {
    const normalizedTagValue = normalizeTag(tag);
    const tagIsSelected = currentFilters.tags.includes(normalizedTagValue);

    updateProductSearchParams({
      tag: tagIsSelected ? null : getDisplayValue(normalizedTagValue, allTags),
    });
  };

  // Count products that match each filter
  const getCategoryCount = (category: string) => {
    return products.filter((product) =>
      getProductCategoryPlacements(product).some((placement) =>
        normalizeTag(placement.category) === normalizeTag(category)
      )
    ).length;
  };

  const getSubcategoryCount = (subcategory: string) => {
    return products.filter((product) =>
      getProductCategoryPlacements(product).some((placement) =>
        normalizeTag(placement.subcategory) === normalizeTag(subcategory) &&
        matchesFilterValues(currentTempFilters.categories, placement.category)
      )
    ).length;
  };

  const getProductTypeCount = (type: string) => {
    return products.filter((product) =>
      getProductCategoryPlacements(product).some((placement) =>
        normalizeTag(getPlacementType(placement)) === normalizeTag(type) &&
        matchesFilterValues(currentTempFilters.categories, placement.category) &&
        matchesFilterValues(currentTempFilters.subcategories, placement.subcategory)
      )
    ).length;
  };

  const getTagCount = (tag: string) => {
    return tagCountMap.get(normalizeTag(tag)) || 0;
  };

  const getSearchCorpus = (product: Product) => {
    return [
      product.name,
      product.category,
      product.subcategory,
      product.productType,
      product.description,
      product.description_extended,
      ...(product.tags || []),
      product.datasheet ? "pds data sheet technical documents download" : "pds data sheet available upon request contact pes",
      product.safetySheet ? "sds safety sheet" : "sds available upon request",
    ]
      .filter(Boolean)
      .map(normalize)
      .join(" ");
  };

  // Filter products based on all criteria
  const filteredProducts = products.filter((product) => {
    const matchesTaxonomy = matchesTaxonomyFilters(product, currentFilters);

    const matchesTags =
      currentFilters.tags.length === 0 ||
      currentFilters.tags.some((tag) =>
        getProductFilterTagKeys(product).includes(tag)
      );

    const searchTokens = normalizeSearchText(searchTerm).split(/\s+/).filter(Boolean);
    const searchCorpus = getSearchCorpus(product);
    const matchesSearch =
      searchTokens.length === 0 ||
      searchTokens.every((token) => searchCorpus.includes(token));

    return (
      matchesTaxonomy && matchesTags && matchesSearch
    );
  });

  // Sort products based on selected option
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortOption) {
      case "type":
        return compareByType(a, b);
      case "newest":
        return b.name.localeCompare(a.name);
      case "relevance":
        if (normalizeSearchText(searchTerm)) {
          const scoreDifference =
            calculateRelevanceScore(b, searchTerm) - calculateRelevanceScore(a, searchTerm);

          if (scoreDifference !== 0) {
            return scoreDifference;
          }

          if (hasLocalPds(a) !== hasLocalPds(b)) {
            return hasLocalPds(a) ? -1 : 1;
          }

          return a.name.localeCompare(b.name, undefined, { numeric: true });
        }

        return compareByType(a, b);
      default:
        return compareByType(a, b);
    }
  });

  // Paginate products
  const indexOfLastProduct = currentPage * itemsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - itemsPerPage;
  const currentProducts = sortedProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);

  // Handle page change
  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    // Scroll to top of products section
    const productsSection = document.getElementById('products-section');
    if (productsSection) {
      productsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Reset current page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchParams, itemsPerPage]);

  const handleProductClick = (product: Product) => {
    const productsSearch = searchParams.toString();
    navigate(`/products/${product.id}`, {
      state: {
        productsSearch: productsSearch ? `?${productsSearch}` : "",
      },
    });
    window.scrollTo(0, 0);
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-red-600 text-white">
        <div className="container mx-auto px-4 py-12 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Products</h1>
            <p className="text-xl">
              Browse Sinopec products available through PES in Canada. Contact us for pricing, current availability, documents, or product matching support.
            </p>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Left Sidebar for Filters */}
            <div className="md:w-1/4 lg:w-1/5">
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-semibold text-lg">Filters</h3>
                    {(currentFilters.categories.length > 0 ||
                      currentFilters.subcategories.length > 0 ||
                      currentFilters.productTypes.length > 0 ||
                      currentFilters.tags.length > 0 ||
                      searchTerm.trim()) && (
                    <button
                      onClick={clearAllFilters}
                      className="text-red-600 text-sm font-medium"
                    >
                      Clear All
                    </button>
                  )}
                </div>

                {/* Category Filter */}
                <div className="mb-6 border-b pb-4">
                  <div
                    className="flex justify-between items-center cursor-pointer mb-3"
                    onClick={() => toggleSection("category")}
                  >
                    <h4 className="font-medium">Category</h4>
                    {expandedSections.category ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </div>

                  {expandedSections.category && (
                    <div className="space-y-2">
                      {allCategories.map((category, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center"
                        >
                          <label className="flex items-center text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={currentFilters.categories.includes(normalizeTag(category))}
                              onChange={() => toggleCategoryFilter(category)}
                              className="form-checkbox h-4 w-4 text-red-600 mr-2"
                            />
                            <span className="capitalize">{category}</span>
                          </label>
                          <span className="text-gray-500 text-xs">
                            {getCategoryCount(category)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Subcategory Filter */}
                <div className="mb-6 border-b pb-4">
                  <div
                    className="flex justify-between items-center cursor-pointer mb-3"
                    onClick={() => toggleSection("subcategory")}
                  >
                    <h4 className="font-medium">Subcategory</h4>
                    {expandedSections.subcategory ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </div>

                  {expandedSections.subcategory && (
                    <div className="space-y-2">
                      {allSubcategories.length > 0 ? (
                        allSubcategories.map((subcategory, index) => (
                          <div
                            key={index}
                            className="flex justify-between items-center"
                          >
                            <label className="flex items-center text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={currentFilters.subcategories.includes(normalizeTag(subcategory))}
                                onChange={() => toggleSubcategoryFilter(subcategory)}
                                className="form-checkbox h-4 w-4 text-red-600 mr-2"
                              />
                              <span>{subcategory}</span>
                            </label>
                            <span className="text-gray-500 text-xs">
                              {getSubcategoryCount(subcategory)}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">Select a category to see subcategories.</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Product Type Filter */}
                <div className="mb-6 border-b pb-4">
                  <div
                    className="flex justify-between items-center cursor-pointer mb-3"
                    onClick={() => toggleSection("productType")}
                  >
                    <h4 className="font-medium">Product Type</h4>
                    {expandedSections.productType ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </div>

                  {expandedSections.productType && (
                    <div className="space-y-2">
                      {allProductTypes.map((type, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center"
                        >
                          <label className="flex items-center text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={currentFilters.productTypes.includes(normalizeTag(type))}
                              onChange={() => toggleProductTypeFilter(type)}
                              className="form-checkbox h-4 w-4 text-red-600 mr-2"
                            />
                            <span>{type}</span>
                          </label>
                          <span className="text-gray-500 text-xs">
                            {getProductTypeCount(type)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Tag Filter */}
                <div className="mb-6 border-b pb-4">
                  <div
                    className="flex justify-between items-center cursor-pointer mb-3"
                    onClick={() => toggleSection("tag")}
                  >
                    <h4 className="font-medium">Tags</h4>
                    {expandedSections.tag ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </div>

                  {expandedSections.tag && (
                    <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                      {allTags.length > 0 ? (
                        allTags.map((tag, index) => (
                          <div
                            key={`${tag}-${index}`}
                            className="flex justify-between items-center"
                          >
                            <label className="flex items-center text-sm cursor-pointer">
                              <input
                                type="checkbox"
                              checked={currentFilters.tags.includes(normalizeTag(tag))}
                              onChange={() => toggleTagFilter(tag)}
                                className="form-checkbox h-4 w-4 text-red-600 mr-2"
                              />
                              <span>{tag}</span>
                            </label>
                            <span className="text-gray-500 text-xs">
                              {getTagCount(tag)}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">No matching tags for the selected filters.</p>
                      )}
                    </div>
                  )}
                </div>

              </div>
            </div>

            {/* Main Content */}
            <div className="md:w-3/4 lg:w-4/5">
              {/* Active filters */}
              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-sm text-gray-700">
                    Active filters:
                  </span>
                  {activeFilters.length === 0 ? (
                    <span className="text-sm text-gray-500">None</span>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {activeFilters.map((filter) => (
                        <div
                          key={`${filter.type}-${normalizeTag(filter.value)}`}
                          className="bg-gray-100 rounded-full px-3 py-1 text-xs flex items-center"
                        >
                          <span>{filter.label}</span>
                          <button
                            onClick={() => {
                              if (filter.type === "search") {
                                updateProductSearchParams({ q: null });
                                return;
                              }

                              if (filter.type === "category") {
                                updateProductSearchParams({
                                  category: null,
                                  subcategory: null,
                                  type: null,
                                  tag: null,
                                });
                                return;
                              }

                              if (filter.type === "subcategory") {
                                updateProductSearchParams({
                                  subcategory: null,
                                  type: null,
                                  tag: null,
                                });
                                return;
                              }

                              if (filter.type === "productType") {
                                updateProductSearchParams({
                                  type: null,
                                  tag: null,
                                });
                                return;
                              }

                              updateProductSearchParams({ tag: null });
                            }}
                            className="ml-1 text-gray-500 hover:text-gray-700"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Search and Sort */}
              <div className="mb-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="relative w-full sm:w-auto">
                    <input
                      type="text"
                      placeholder="Search products"
                      value={searchTerm}
                      onChange={(e) => updateProductSearchParams({ q: e.target.value })}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full sm:w-64 focus:ring-red-500 focus:border-red-500"
                    />
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between w-full sm:w-auto">
                    <div className="flex items-center">
                      <label htmlFor="sort" className="text-sm font-medium text-gray-600 mr-2">
                        Sort by:
                      </label>
                      <select
                        id="sort"
                        value={sortOption}
                        onChange={(e) => updateProductSearchParams({ sort: e.target.value })}
                        className="border border-gray-300 rounded-md p-1 text-sm"
                      >
                        <option value="type">Type</option>
                        <option value="relevance">Relevance</option>
                        <option value="newest">Newest</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Products Grid */}
              <div id="products-section" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortedProducts.length > 0 ? (
                  currentProducts.map((product) => (
                    <div
                      key={product.id}
                      className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => handleProductClick(product)}
                    >
                      <div className="h-48 bg-gray-50 flex items-center justify-center p-4">
                        <img
                          src={product.image || "/placeholder.svg"}
                          alt={product.name}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="p-4">
                        <div className="mb-2">
                          <span className="inline-block bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-md capitalize">
                            {product.category}
                          </span>
                          <span className="inline-block bg-gray-100 text-gray-800 text-xs font-medium px-2 py-1 rounded-md ml-2">
                            {product.subcategory || getProductType(product)}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold mb-2">
                          {product.name}
                        </h3>
                        <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                          {product.description}
                        </p>
                        <div className="mb-3 text-xs text-gray-500">
                            {isDownloadableDocument(product.datasheet) ? (
                            <a
                              href={product.datasheet}
                              download
                              className="text-red-600 hover:text-red-700 font-medium"
                              onClick={(event) => event.stopPropagation()}
                            >
                              Download Data Sheet
                            </a>
                          ) : (
                            <span>Data Sheet available upon request</span>
                          )}
                        </div>
                        <div className="flex flex-wrap justify-between items-center gap-3">
                          <Link
                            to="/contact"
                            className="text-gray-700 hover:text-red-600 font-medium text-sm"
                            onClick={(event) => event.stopPropagation()}
                          >
                            Request a Quote
                          </Link>
                          <button className="text-red-600 hover:text-red-700 font-medium text-sm flex items-center">
                            View Product Details
                            <svg
                              className="w-4 h-4 ml-1"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 5l7 7-7 7"
                              ></path>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full text-center py-12">
                    <p className="text-gray-500 text-lg">
                        No matching products found.
                    </p>
                    <button
                      onClick={clearAllFilters}
                      className="mt-4 text-red-600 hover:text-red-700 font-medium"
                    >
                      Clear filters
                    </button>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {sortedProducts.length > 0 && totalPages > 1 && (
                <div className="flex justify-center items-center mt-8">
                  <nav className="flex flex-wrap items-center justify-center">
                    <button
                      onClick={() => currentPage > 1 && paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`mx-1 my-1 px-3 py-2 rounded-md ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Previous
                    </button>
                    
                    {[...Array(totalPages)].map((_, index) => {
                      // Show fewer pages on mobile
                      const pageNumber = index + 1;
                      
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (!isSmallScreen && (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)) ||
                        (isSmallScreen && pageNumber === currentPage)
                      ) {
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => paginate(pageNumber)}
                            className={`mx-1 my-1 px-3 py-2 rounded-md ${
                              currentPage === pageNumber
                                ? 'bg-red-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      } else if (
                        (pageNumber === 2 && currentPage > 3) ||
                        (pageNumber === totalPages - 1 && currentPage < totalPages - 2)
                      ) {
                        return <span key={pageNumber} className="mx-1 my-1">...</span>;
                      }
                      return null;
                    })}
                    
                    <button
                      onClick={() => currentPage < totalPages && paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`mx-1 my-1 px-3 py-2 rounded-md ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Next
                    </button>
                  </nav>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ProductsPage;
